import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';

const SubscriptionContext = createContext(null);

// Pages that expired trial users can still access
const ALLOWED_WHEN_EXPIRED = ['/pricing', '/corporate', '/settings', '/login', '/register'];

export const SubscriptionProvider = ({ children }) => {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  let navigate, location;
  try {
    navigate = useNavigate();
    location = useLocation();
  } catch (e) {
    // Outside router context — skip redirect logic
    navigate = null;
    location = null;
  }

  const fetchSubscription = useCallback(async () => {
    if (!user?.token) {
      setSubscription(null);
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/subscription/me`,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      setSubscription(response.data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
      setSubscription({ tier: 'free', status: 'free', features: [] });
    } finally {
      setLoading(false);
    }
  }, [user?.token, BACKEND_URL]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  // Redirect expired trial users to pricing page
  useEffect(() => {
    if (!subscription || !navigate || !location) return;
    if (user?.role === 'admin') return; // Admin never gets redirected
    
    if (subscription.status === 'expired') {
      const currentPath = location.pathname;
      const isAllowed = ALLOWED_WHEN_EXPIRED.some(p => currentPath.startsWith(p));
      if (!isAllowed) {
        navigate('/pricing', { replace: true, state: { trialExpired: true } });
      }
    }
  }, [subscription, navigate, location, user?.role]);

  const hasFeature = useCallback((featureName) => {
    if (!subscription) return false;

    const tier = subscription.tier;

    // Admin and reviewer always have access to all features
    if (user?.role === 'admin' || user?.role === 'reviewer') return true;

    // single_stocks requires pro_max tier
    if (featureName === 'single_stocks') {
      return tier === 'pro_max' && ['trial', 'active'].includes(subscription.status);
    }

    // Free tier has no pro features
    if (tier === 'free') return false;

    // Pro, Student, Family, Pro Max have all standard pro features
    if (['pro', 'student', 'family', 'pro_max'].includes(tier)) {
      return ['trial', 'active'].includes(subscription.status);
    }

    // A La Carte: check selected features
    if (tier === 'a_la_carte') {
      return (subscription.selected_features || []).includes(featureName) &&
             ['trial', 'active'].includes(subscription.status);
    }

    return false;
  }, [subscription, user?.role]);

  return (
    <SubscriptionContext.Provider value={{
      subscription,
      loading,
      hasFeature,
      refreshSubscription: fetchSubscription,
      tier: subscription?.tier || 'free',
      status: subscription?.status || 'free',
      familyRole: subscription?.family_role || null,
      familyId: subscription?.family_id || null
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within SubscriptionProvider');
  }
  return context;
};
